import { useState, useEffect, useRef } from 'react'
import { Icon } from 'assets/index'
import { startOfDay, endOfDay } from 'date-fns'
import handleTaskDatesClick from './handleTaskDatesClick'
import {
  MainContainer,
  FakeInput,
  Menu,
  Placeholder,
} from './SearchDatesStyles'
import DataPicker from '../DataPicker/DataPicker'

export interface DateRange {
  startDate: Date | null
  endDate: Date | null
}

interface SearchDatesProps {
  dateRange: DateRange
  showDataPicker: boolean
  setShowDataPicker: React.Dispatch<React.SetStateAction<boolean>>
  setDateRange: React.Dispatch<React.SetStateAction<DateRange>>
}

function SearchDates({
  dateRange,
  setDateRange,
  showDataPicker,
  setShowDataPicker,
}: SearchDatesProps) {
  const [showMenu, setShowMenu] = useState(false)

  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth())
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear())

  const containerRef = useRef<HTMLFormElement>(null)

  // Fecha o menu e o calendário ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowMenu(false)
        setShowDataPicker(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    handleTaskDatesClick()
  }

  const formatDatepicker = (date: Date | null): string => {
    if (!date) return ''
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = String(date.getFullYear() % 100).padStart(2, '0')
    return `${day}/${month}/${year}`
  }

  const today = new Date()

  const todayOption = {
    startDate: startOfDay(today),
    endDate: null,
  }
  const sevenOption = {
    startDate: startOfDay(new Date(new Date().setDate(today.getDate() - 6))),
    endDate: endOfDay(new Date()),
  }
  const fifteenOption = {
    startDate: startOfDay(new Date(new Date().setDate(today.getDate() - 14))),
    endDate: endOfDay(new Date()),
  }
  const handleMenuClick = (option: string) => {
    switch (option) {
      case 'Hoje':
        setDateRange(todayOption)
        setShowDataPicker(false)
        break
      case 'Últimos 7 dias':
        setDateRange(sevenOption)
        setShowDataPicker(false)
        break
      case 'Últimos 15 dias':
        setDateRange(fifteenOption)
        setShowDataPicker(false)
        break
      case 'clear':
        setDateRange({ startDate: null, endDate: null })
        setShowDataPicker(false)
        break
      case 'Personalizado':
        setShowDataPicker(true)
        return
      default:
        return
    }

    setShowMenu(false)
  }

  const isSelectedRange = (compare: DateRange) => {
    return (
      dateRange.startDate?.toDateString() ===
        compare.startDate?.toDateString() &&
      (dateRange.endDate?.toDateString?.() || null) ===
        (compare.endDate?.toDateString?.() || null)
    )
  }
  return (
    <MainContainer
      className="dates-main-container"
      ref={containerRef}
      onSubmit={handleSubmit}
    >
      <div
        className="input-container"
        role="button"
        tabIndex={0}
        onClick={() => {
          setShowMenu((prev) => !prev)
          setShowDataPicker(false) // fecha o calendário ao abrir o menu
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setShowMenu((prev) => !prev)
            setShowDataPicker(false)
          }
        }}
        aria-pressed={showMenu}
      >
        <img className="doc" src={Icon('calendar.svg')} alt="filter icon" />
        <FakeInput>{formatDatepicker(dateRange.startDate)}</FakeInput>
        <FakeInput>{formatDatepicker(dateRange.endDate)}</FakeInput>
        <img
          className="chrevon-down"
          src={Icon('chevron_down.svg')}
          alt="toggle calendar"
        />
        <Placeholder $isValid={!!dateRange.startDate || !!dateRange.endDate}>
          Datas
        </Placeholder>
      </div>

      {showMenu && (
        <Menu>
          <button
            className={`option${
              (isSelectedRange(todayOption) && ' selected') || ''
            }`}
            type="button"
            onClick={() => handleMenuClick('Hoje')}
          >
            Hoje
          </button>

          <button
            className={`option${
              (isSelectedRange(sevenOption) && ' selected') || ''
            }`}
            type="button"
            onClick={() => handleMenuClick('Últimos 7 dias')}
          >
            Últimos 7 dias
          </button>

          <button
            className={`option${
              (isSelectedRange(fifteenOption) && ' selected') || ''
            }`}
            type="button"
            onClick={() => handleMenuClick('Últimos 15 dias')}
          >
            Últimos 15 dias
          </button>

          <button
            className={`option`}
            type="button"
            onClick={() => handleMenuClick('clear')}
          >
            Limpar
          </button>

          <button
            className={`option${(showDataPicker && ' selected') || ''}`}
            type="button"
            onClick={() => handleMenuClick('Personalizado')}
          >
            Personalizado
          </button>
        </Menu>
      )}

      {showDataPicker && (
        <DataPicker
          dateRange={dateRange}
          setDateRange={setDateRange}
          showDataPicker={showDataPicker}
          currentMonth={currentMonth}
          setCurrentMonth={setCurrentMonth}
          currentYear={currentYear}
          setCurrentYear={setCurrentYear}
        />
      )}
    </MainContainer>
  )
}

export default SearchDates
